/**
 * StaticControlRow - Shared component for non-interactive, comma-separated control display.
 *
 * Renders all controls as compact text, separated by commas. When the content wraps to
 * multiple lines, commas appear at the end of each line (not as leading commas on new lines).
 * Prioritizes space efficiency over interactive functionality.
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';
import {twMerge} from 'tailwind-merge';
import useResizeObserver from 'use-resize-observer';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type ControlDefinition, type GridData} from '../types';

export type StaticControlRowProps = {
  controls: ControlDefinition[];
  columnIndex?: number;
  disableRunLinks?: boolean;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
  mediaType: MediaCardType;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {width: number; height: number};
  gridData: GridData;
  isFullScreenPanelPage: boolean;
  keyPrefix: string;
};

export const StaticControlRow: React.FC<StaticControlRowProps> = ({
  controls,
  columnIndex,
  disableRunLinks,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  gridData,
  isFullScreenPanelPage,
  keyPrefix,
}) => {
  const {ref: staticControlRowRef, width: staticControlRowWidth} =
    useResizeObserver<HTMLDivElement>();
  const isCompareMode = config.mode === 'compare';
  const containerClasses = twMerge(
    'flex flex-wrap items-center justify-center',
    'text-sm/5',
    'pt-8 px-8',
    'max-w-full',
    isCompareMode ? 'gap-8' : 'gap-16' // Remove gaps, use manual spacing with commas before each item (except first)
  );

  return (
    <div className={containerClasses} ref={staticControlRowRef}>
      {controls.map((control, idx) => (
        <div
          key={`${keyPrefix}-${control.control}${columnIndex ? `-${columnIndex}` : ''}`}
          className="flex items-center min-w-0">
          <div className="min-w-0 flex-1">
            <ControlRenderer
              control={control}
              index={idx}
              config={config}
              stepMeta={stepMeta}
              columnIndex={columnIndex}
              disableRunLinks={disableRunLinks}
              updateConfig={updateConfig}
              isMaxStepLoading={isMaxStepLoading}
              mediaBrowserSize={mediaBrowserSize}
              gridData={gridData}
              contentFooterWidth={staticControlRowWidth}
              isFullScreenPanelPage={isFullScreenPanelPage}
              layout="horizontal"
            />
          </div>
          {config.mode === 'compare' && idx < controls.length - 1 && (
            <span className="flex-shrink-0 select-none ml-2 mr-2">,</span>
          )}
        </div>
      ))}
    </div>
  );
};
